import React, { useEffect, useState } from 'react';
import { Col, notification } from 'antd';
import { useNavigate, useLocation } from 'react-router';

// components
import { ScreenWrapperComponent } from 'components/layouts/ScreenWrapperComponent';
import { TabsComponent } from 'baseComponents/TabsComponent';
import { ButtonComponent } from 'baseComponents/ButtonComponent';
import { FullScreenLoaderComponent } from 'baseComponents/FullScreenLoaderComponent';
import { SuccessScreen } from 'components/SuccessScreen';

// tabs
import { SchedulingOptionsTab } from './tabs/SchedulingOptionsTab';
import { ComposeTab } from './tabs/ComposeTabSms';
import { AudienceTab } from './tabs/AudienceTab';
import { PreviewCampaign } from './tabs/PreviewCampaign';

// types
import { TabItem } from 'types/componentTypes/tabs-component-types';

// store
import { useCreateNewAudienceActions } from 'store/CreateNewAudienceStore';
import {
  useCreateCampaignStore,
  useCreateCampaignStoreActions,
} from 'store/CreateCampaignStore';

// hooks
import {
  CampaignEstimationsResponseType,
  useEstimateAudience,
} from 'hooks/useEstimateAudience';
import { useCreateNewCampaign } from 'hooks/useCreateNewCampaign';

// constants
import { CREATE_CAMPAIGN_SCREEN } from 'constants/enums/create-campaign-screen';

// helpers
import {
  campaignMessagePayload,
  convertSendDate,
  isCampaignIncomplete,
} from 'helpers/create-campaign';

// styles
import 'screens/CreateCampaignScreen/createCampaignScreen.scss';
import { ResultPage } from 'components/ResultPage';
import { checkIfTemplateIsBlank, ComposeTabPush } from './tabs/ComposeTabPush';

export const CreateCampaignScreen: React.FC = () => {
  const [api, contextHolder] = notification.useNotification();
  const [activeTab, setActiveTab] = useState<string>(
    CREATE_CAMPAIGN_SCREEN.CAMPAIGN_SETUP_TAB_KEY
  );

  const [estimatedReachableUsers, setEstimatedReachableUsers] = useState('');

  // zustand store
  const storeState = useCreateCampaignStore((state) => ({
    name: state.name,
    stores: state.stores,
    sendDate: state.sendDate,
    sendTime: state.sendTime,
    audience: state.audience,
    message: state.message,
    messageTemplate: state.messageTemplate,
    messageEncoding: state.messageEncoding,
    previewMessage: state.previewMessage,
    messagesQuantity: state.messagesQuantity,
    estimatedReachableUsers: 0,
    hasCredits: state.hasCredits,
    selectedChannel: state.selectedChannel,
    title: state.title,
  }));
  const { clearCreateNewAudienceStore } = useCreateNewAudienceActions();
  const { reset: clearCreateCampaignStore, setSelectedChannel } =
    useCreateCampaignStoreActions();

  const location = useLocation();
  const redirectFromPayment = location.state?.redirectFromPayment;

  useEffect(() => {
    if (!redirectFromPayment) {
      clearCreateNewAudienceStore();
      clearCreateCampaignStore();
    }
    if (redirectFromPayment) {
      setActiveTab(CREATE_CAMPAIGN_SCREEN.AUDIENCE_TAB_KEY);
    }
    // eslint-disable-next-line
  }, [redirectFromPayment]);

  const createNewCampaign = useCreateNewCampaign({
    onError: (e) =>
      api.error({
        message: e?.message || 'Something went wrong',
        description:
          'Something went wrong with creating campaign. Please try again later.',
        duration: 3,
      }),
  });
  const navigate = useNavigate();

  const isCampaignSetup =
    !storeState.sendDate ||
    !storeState.sendTime ||
    storeState.name.length === 0;

  const isTemplateSelected = !!storeState.messageTemplate;
  console.log('storeState.message', storeState.message);
  console.log('storeState.title', storeState.title);
  const isAudienceSelected = !!storeState.audience?.id && storeState.hasCredits;
  const disableNextButton = () => {
    if (activeTab === CREATE_CAMPAIGN_SCREEN.CAMPAIGN_SETUP_TAB_KEY) {
      return isCampaignSetup;
    }
    if (
      activeTab === CREATE_CAMPAIGN_SCREEN.COMPOSE_TAB_KEY &&
      storeState.selectedChannel === 'push' &&
      checkIfTemplateIsBlank(
        storeState.messageTemplate?.tags,
        storeState.messageTemplate?.name
      )
    ) {
      return storeState.message.length === 0 || storeState.title.length === 0;
    }
    if (
      activeTab === CREATE_CAMPAIGN_SCREEN.COMPOSE_TAB_KEY &&
      storeState.selectedChannel === 'push'
    ) {
      return !isTemplateSelected;
    }
    if (
      activeTab === CREATE_CAMPAIGN_SCREEN.AUDIENCE_TAB_KEY ||
      activeTab === CREATE_CAMPAIGN_SCREEN.COMPOSE_TAB_KEY ||
      activeTab === CREATE_CAMPAIGN_SCREEN.PREVIEW_CAMPAIGN_TAB_KEY
    ) {
      return (
        isCampaignSetup || !isAudienceSelected || !storeState.selectedChannel
      );
    }
  };

  const creditCost = () => {
    if (
      estimatedReachableUsers.length > 0 &&
      storeState.selectedChannel === 'sms'
    ) {
      return (
        parseInt(estimatedReachableUsers, 10) *
        parseInt(storeState.messagesQuantity, 10)
      ).toString();
    } else if (
      estimatedReachableUsers.length > 0 &&
      storeState.selectedChannel === 'push'
    ) {
      return estimatedReachableUsers;
    }
    return '0';
  };

  const onEstimateAudienceSuccess = (data: CampaignEstimationsResponseType) => {
    if (!storeState.selectedChannel) {
      throw new Error('Selected channel is not set');
    }
    if (storeState.selectedChannel === 'sms') {
      setEstimatedReachableUsers(
        data[storeState.selectedChannel].estimated_reachable_users.toString()
      );
    } else if (storeState.selectedChannel === 'push') {
      setEstimatedReachableUsers(
        data[storeState.selectedChannel].credit_cost.toString()
      );
    }

    return api.success({
      message: 'Estimation successful',
      description: 'Campaign has been estimated successfully',
      duration: 3,
    });
  };

  const handleEstimateAudienceError = (data: any) => {
    return api.error({
      message: data?.message || 'Something went wrong',
      description:
        'Something went wrong with estimating audience. Please try again later.',
      duration: 3,
    });
  };

  const estimateAudience = useEstimateAudience({
    onSuccess: onEstimateAudienceSuccess,
    onError: handleEstimateAudienceError,
  });

  const getSendDateTimestamp = () => {
    if (storeState.sendDate && storeState.sendTime) {
      return convertSendDate(storeState.sendDate, storeState.sendTime);
    }
    return null;
  };

  const handleTabChange = (key: string) => {
    const sendDateTimestamp = getSendDateTimestamp();
    if (
      key === CREATE_CAMPAIGN_SCREEN.PREVIEW_CAMPAIGN_TAB_KEY &&
      sendDateTimestamp
    ) {
      const data = {
        audience_filters: storeState.audience?.audience_filters,
        message_length: storeState.previewMessage.length,
        message_encoding: storeState.messageEncoding,
        store_ids: storeState.stores.map(Number),
        scheduled_time: sendDateTimestamp,
      };
      estimateAudience.mutate(data);
    }
    setActiveTab(key);
  };

  const handleScheduleCampaign = () => {
    const sendDateTimestamp = getSendDateTimestamp();
    const campaignAudienceId = storeState.audience?.id;
    const campaignMessageId = storeState.messageTemplate
      ? storeState.messageTemplate.campaign_id
      : null;
    const payloadMessage = campaignMessagePayload(
      storeState.message,
      campaignMessageId,
      storeState.title || null
    );
    const payloadCampaignStores = storeState.stores.map(Number);
    const payload = {
      name: storeState.name,
      store_ids: payloadCampaignStores,
      audience_id: campaignAudienceId,
      scheduled_time: sendDateTimestamp,
      message: payloadMessage,
      segments: parseInt(storeState.messagesQuantity),
      channel: storeState.selectedChannel,
    };
    createNewCampaign.mutate(payload);
  };

  const handleNextButton = () => {
    if (activeTab === CREATE_CAMPAIGN_SCREEN.CAMPAIGN_SETUP_TAB_KEY) {
      setActiveTab(CREATE_CAMPAIGN_SCREEN.AUDIENCE_TAB_KEY);
    } else if (activeTab === CREATE_CAMPAIGN_SCREEN.AUDIENCE_TAB_KEY) {
      setActiveTab(CREATE_CAMPAIGN_SCREEN.COMPOSE_TAB_KEY);
    } else if (activeTab === CREATE_CAMPAIGN_SCREEN.COMPOSE_TAB_KEY) {
      const sendDateTimestamp = getSendDateTimestamp();
      if (sendDateTimestamp && storeState.selectedChannel === 'sms') {
        const data = {
          audience_filters: storeState.audience?.audience_filters,
          message_length: storeState.previewMessage.length,
          message_encoding: storeState.messageEncoding,
          store_ids: storeState.stores.map(Number),
          scheduled_time: sendDateTimestamp,
        };
        estimateAudience.mutate(data);
      } else if (sendDateTimestamp && storeState.selectedChannel === 'push') {
        const data = {
          audience_filters: storeState.audience?.audience_filters,
          message_length: storeState.previewMessage.length,
          message_encoding: storeState.messageEncoding,
          store_ids: storeState.stores.map(Number),
          scheduled_time: sendDateTimestamp,
        };
        estimateAudience.mutate(data);
      }
      setActiveTab(CREATE_CAMPAIGN_SCREEN.PREVIEW_CAMPAIGN_TAB_KEY);
    }
  };

  const disableScheduleCampaign = isCampaignIncomplete(storeState);

  const tabsItems: TabItem[] = [
    {
      label: CREATE_CAMPAIGN_SCREEN.CAMPAIGN_SETUP,
      key: CREATE_CAMPAIGN_SCREEN.CAMPAIGN_SETUP_TAB_KEY,
      children: <SchedulingOptionsTab />,
    },
    {
      label: CREATE_CAMPAIGN_SCREEN.AUDIENCE,
      key: CREATE_CAMPAIGN_SCREEN.AUDIENCE_TAB_KEY,
      children: (
        <AudienceTab
          onChannelSelect={(channel) => {
            setSelectedChannel(channel);
            handleNextButton();
          }}
        />
      ),
      disabled: isCampaignSetup,
    },
    {
      label: CREATE_CAMPAIGN_SCREEN.COMPOSE,
      key: CREATE_CAMPAIGN_SCREEN.COMPOSE_TAB_KEY,
      children: (() => {
        switch (storeState.selectedChannel) {
          case 'sms':
            return <ComposeTab />;
          case 'push':
            return <ComposeTabPush />;
          default:
            return null;
        }
      })(),
      disabled:
        isCampaignSetup || !isAudienceSelected || !storeState.selectedChannel,
    },
    {
      label: CREATE_CAMPAIGN_SCREEN.PREVIEW_CAMPAIGN,
      key: CREATE_CAMPAIGN_SCREEN.PREVIEW_CAMPAIGN_TAB_KEY,
      children: (
        <PreviewCampaign
          estimatedCampaignCost={creditCost()}
          selectedChannel={storeState.selectedChannel!}
        />
      ),
      disabled:
        isCampaignSetup || !isAudienceSelected || !storeState.selectedChannel,
    },
  ];

  if (createNewCampaign.isError) {
    return <ResultPage title={createNewCampaign.error.message} />;
  }

  if (createNewCampaign.isSuccess) {
    return (
      <SuccessScreen
        successTitle='Campaign created'
        successSubtitle='Campaign has been successfully scheduled.'
        buttonLabel='Go back to Dashboard'
      />
    );
  }

  return (
    <ScreenWrapperComponent>
      {contextHolder}
      <Col
        lg={{ pull: 3, push: 3, span: 18 }}
        md={{ pull: 1, push: 1, span: 22 }}
      >
        <div className='create-campaign-main-section'>
          <TabsComponent
            onChange={(key) => handleTabChange(key)}
            items={tabsItems}
            activeKey={activeTab}
          />
        </div>
        <Col
          lg={{ pull: 5, push: 5, span: 14 }}
          md={{ pull: 1, push: 1, span: 22 }}
        >
          <div className='campaign-preview-buttons-wrapper'>
            {activeTab === CREATE_CAMPAIGN_SCREEN.PREVIEW_CAMPAIGN_TAB_KEY ? (
              <div className='button'>
                <ButtonComponent
                  appereance='primary'
                  label={CREATE_CAMPAIGN_SCREEN.SCHEDULE}
                  onClick={handleScheduleCampaign}
                  size='middle'
                  fullWidth
                  disabled={disableScheduleCampaign}
                />
              </div>
            ) : (
              <>
                <div className='button'>
                  <ButtonComponent
                    appereance='secondary'
                    label={CREATE_CAMPAIGN_SCREEN.CANCEL}
                    onClick={() => navigate('/home')}
                    size='middle'
                    fullWidth
                  />
                </div>
                {activeTab !== CREATE_CAMPAIGN_SCREEN.AUDIENCE_TAB_KEY && (
                  <div className='button'>
                    <ButtonComponent
                      appereance='primary'
                      label={CREATE_CAMPAIGN_SCREEN.NEXT}
                      onClick={handleNextButton}
                      size='middle'
                      fullWidth
                      disabled={disableNextButton()}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </Col>
      </Col>
      <FullScreenLoaderComponent spinning={createNewCampaign.isPending} />
    </ScreenWrapperComponent>
  );
};
